// /account/wallet page. Story 1.17 built it; Story 8.2 added the second pot;
// Story 8.3 removed everything to do with taking money OUT.
//
// Hosts the full-variant <BalanceDisplay>, the wallet-details identity form,
// and a stub for the transaction history list. The provenance breakdown
// segments fed to BalanceDisplay are derived from the first page of
// /wallet/history.
//
// WHAT IS NOT HERE ANY MORE, AND MUST NOT COME BACK: the CashoutModal, the
// bank-account section, and the payment-account status query that drove the
// "Link bank to cash out" branch. A topped-up balance is closed-loop and
// non-payable (owner ruling 2026-08-20, NP+C). The bank-account panel moved
// to `features/creator-payout/`, unmounted, as Story 8.5 stock — read that
// module's header before putting it on any surface.
//
// The one action that DOES move money between pots is the earnings
// conversion, and it is not a payout: it is one-way, free, and stays inside
// the platform.

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { BalanceDisplay, type ProvenanceBreakdownSegment } from "@greenhold/components";

import { attachRequestIdToScope } from "../lib/api-client";
import { ConvertEarningsModal } from "../features/earnings/ConvertEarningsModal";
import { WalletDetailsPanel } from "../features/wallet-details/WalletDetailsPanel";
import { TopUpModal } from "../features/top-up/TopUpModal";
import { useHeldOfferFunds } from "../features/refund/useHeldOfferFunds";
import { useRefundEligibleList } from "../features/refund/useRefundEligibleList";
// Story 11.4 — the history rows, each top-up carrying a link to its receipt.
import {
  RecentActivityList,
  type LedgerHistoryRow,
} from "../features/wallet-history/RecentActivityList";

type BalanceSnapshot = {
  balance_cents: number;
  escrow_cents: number;
  // Story 8.2 — the EARNED pot, kept separate from the wallet pot above and
  // never added to it.
  earnings_cents?: number;
  currency: "EUR";
};

type HistoryResponse = {
  // Story 11.4 — one row shape, defined next to the list that renders it, so
  // the receipt fields cannot be declared in one place and forgotten in the
  // other.
  data: LedgerHistoryRow[];
  meta: { has_more: boolean; next_before?: string | null };
};

async function fetchBalance(): Promise<BalanceSnapshot> {
  const response = await fetch("/api/v1/wallet/balance", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) throw new Error(`balance fetch failed: ${response.status}`);
  return (await response.json()) as BalanceSnapshot;
}

async function fetchHistory(): Promise<HistoryResponse> {
  const response = await fetch("/api/v1/wallet/history?limit=100", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) throw new Error(`history fetch failed: ${response.status}`);
  return (await response.json()) as HistoryResponse;
}

function deriveProvenanceBreakdown(
  history: HistoryResponse | undefined,
  balanceCents: number,
): ProvenanceBreakdownSegment[] | undefined {
  if (!history || balanceCents <= 0) return undefined;
  let topUpCents = 0;
  let marketplaceCents = 0;
  for (const entry of history.data) {
    if (entry.direction !== "credit") continue;
    if (entry.provenance === "top_up") topUpCents += entry.amount_cents;
    else if (entry.provenance === "marketplace_sale")
      marketplaceCents += entry.amount_cents;
  }
  const total = topUpCents + marketplaceCents;
  if (total === 0) return undefined;
  return [
    {
      label: "From top-ups",
      ratio: topUpCents / total,
      colorClass: "bg-brand-forest-700",
    },
    {
      label: "From marketplace sales",
      ratio: marketplaceCents / total,
      colorClass: "bg-brand-gold-500",
    },
  ].filter((seg) => seg.ratio > 0);
}

export function WalletPage() {
  const [topupOpen, setTopupOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);

  const refundEligible = useRefundEligibleList();
  const heldInOffers = useHeldOfferFunds();
  const balance = useQuery({
    queryKey: ["wallet-balance"],
    queryFn: fetchBalance,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
  const history = useQuery({
    queryKey: ["wallet-history", "first-page"],
    queryFn: fetchHistory,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });

  const provenanceBreakdown = useMemo(
    () => deriveProvenanceBreakdown(history.data, balance.data?.balance_cents ?? 0),
    [history.data, balance.data?.balance_cents],
  );

  // Hide the refund path ONLY when we have PROVEN there is nothing behind it.
  // Gating on `items.length > 0` alone also hid it whenever the probe FAILED —
  // an errored query is not proof of an empty wallet, and legal action item 12
  // does not let a network blip remove the refund-to-source link. Show it on
  // doubt; the refund page itself explains what it finds.
  const eligibleCount = refundEligible.data?.items.length ?? 0;
  const refundStateUnknown = refundEligible.isError || heldInOffers.isError;
  const showRefundLink =
    eligibleCount > 0 || heldInOffers.heldCents > 0 || refundStateUnknown;

  // Story 8.2 — the second pot. Read separately, rendered separately, and
  // never combined with the figure above: only this one is payable.
  const earningsCents = balance.data?.earnings_cents ?? 0;

  // Money flows (top-up, and the refund rail) answer 412
  // `wallet_details_required` until the identity form is complete. Rather
  // than surface that as a raw error, they call this to bring the user to
  // the wallet-details section and focus its first field; after saving,
  // the user retries the original action.
  const scrollToWalletDetails = () => {
    const el = document.getElementById("wallet-details-section");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    const firstField = document.getElementById(
      "wallet-details-first-name",
    ) as HTMLInputElement | null;
    firstField?.focus();
  };

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-[var(--gh-rhythm-consumer,24px)] p-6 text-brand-paper-100">
      <header>
        <h1 className="font-heading text-4xl font-bold">Your Greenhold wallet</h1>
        <p className="font-body text-sm opacity-80">
          Top up, spend on Greenhold, and review your full transaction
          history.
        </p>
      </header>

      <section aria-labelledby="balance-heading">
        <h2 id="balance-heading" className="sr-only">
          Current balance
        </h2>
        <BalanceDisplay
          variant="full"
          onTopupClick={() => setTopupOpen(true)}
          provenanceBreakdown={provenanceBreakdown}
          onConvertEarningsClick={
            earningsCents > 0 ? () => setConvertOpen(true) : undefined
          }
          onResponse={attachRequestIdToScope}
          historyHref="/account/wallet/history"
        />
        {/* An offer hold zeroes every refundable row, so gating this link on
            a non-empty eligible list hid the refund path from exactly the
            users whose 14-day window was running out. Show it whenever money
            is held too — the page itself explains the hold. */}
        {showRefundLink && (
          <p className="mt-3 text-sm">
            <Link
              to="/account/wallet/refund"
              data-testid="wallet-refund-link"
              className="underline decoration-dotted"
            >
              Refund a recent top-up →
            </Link>
          </p>
        )}
      </section>

      <section
        id="wallet-details-section"
        aria-labelledby="wallet-details-heading"
        className="rounded-lg border bg-card p-6 text-card-foreground"
      >
        <h2
          id="wallet-details-heading"
          className="mb-3 font-heading text-xl font-bold"
        >
          Wallet details
        </h2>
        <WalletDetailsPanel />
      </section>

      <section
        aria-labelledby="recent-activity-heading"
        className="rounded-lg border bg-card p-6 text-card-foreground"
      >
        <h2
          id="recent-activity-heading"
          className="mb-3 font-heading text-xl font-bold"
        >
          Recent activity
        </h2>
        {/* Story 11.4 — the rows, and the receipt link on each top-up. The
            page already had this page of history in hand (it derives the
            provenance breakdown from it); until now it rendered a placeholder
            over the top, which left the receipt with nowhere to be reached
            from. */}
        {history.isPending && (
          <p className="font-body text-sm text-muted-foreground">
            Loading your recent activity…
          </p>
        )}
        {history.isError && (
          <p className="font-body text-sm text-destructive" role="alert">
            Couldn&apos;t load your recent activity. Refresh to try again.
          </p>
        )}
        {history.data && <RecentActivityList rows={history.data.data} />}
      </section>

      <TopUpModal
        open={topupOpen}
        onOpenChange={setTopupOpen}
        onRequiresWalletDetails={scrollToWalletDetails}
      />
      <ConvertEarningsModal
        open={convertOpen}
        onOpenChange={setConvertOpen}
        earningsCents={earningsCents}
      />
    </main>
  );
}
